/**
 * 都市商务模板 - VIP会员开通页
 *
 * 设计核心理念：深藏青 Hero + 特权横幅 + 深色实底推荐套餐卡
 * 与 sunset（金色华丽Hero + 功能对比表）完全不同的排版骨架：
 * - 顶部：深藏青渐变 Hero（居中标题 + 3个特权徽章横排）
 * - 中段：3栏套餐卡片（推荐卡为深藏青实底反白样式）
 * - 底部：服务说明
 */

import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/templates/urban/components/Navbar";
import UrbanFooter from "@/templates/urban/components/Footer";
import { useAuth } from "@/hooks/useAuth";
import { getVipPlans } from "@/utils/adminApi";

// ==================== API 层 ====================

interface PaymentConfig {
  alipay_enabled: boolean;
  wechat_enabled: boolean;
  qrcode_enabled: boolean;
  qrcode_priority: boolean;
  alipay_qrcode: string;
  wechat_qrcode: string;
}

async function fetchPaymentConfig(): Promise<PaymentConfig | null> {
  try {
    const token = localStorage.getItem("admin_token") || localStorage.getItem("token");
    const siteId = localStorage.getItem('current_site_id') || '';
    const res = await fetch("/api/admin/settings/payment", {
      method: "GET",
      headers: {
        "Authorization": token ? `Bearer ${token}` : "",
        'X-Site-Id': siteId,
      },
    });
    const data = await res.json();
    if (data.code === 0 && data.data) {
      return {
        alipay_enabled: !!data.data.alipay_enabled,
        wechat_enabled: !!data.data.wechat_enabled,
        qrcode_enabled: !!data.data.qrcode_enabled,
        qrcode_priority: !!data.data.qrcode_priority,
        alipay_qrcode: data.data.alipay_qrcode || "",
        wechat_qrcode: data.data.wechat_qrcode || "",
      };
    }
    return null;
  } catch (e) {
    console.error("读取支付配置失败", e);
    return null;
  }
}

export default function UrbanVipPage() {
  const { isLoggedIn } = useAuth();

  const [packages, setPackages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<string | number>(1);
  const [payMethod, setPayMethod] = useState<"wechat" | "alipay" | "qrcode">("wechat");
  const [showPayModal, setShowPayModal] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState<PaymentConfig | null>(null);

  useEffect(() => {
    Promise.all([
      getVipPlans().then((data) => {
        // 将后台数据映射为前端展示格式
        if (Array.isArray(data) && data.length > 0) {
          const mapped = data.map((p: any) => ({
            id: p.id,
            name: p.name || "VIP套餐",
            price: String(p.amount ?? p.price ?? 0),
            duration: (p.duration_months ?? p.duration ?? 1) * 30,  // 月×30=天
            duration_months: p.duration_months ?? p.duration ?? 1,
            description: p.description || "",
            is_recommended: !!(p.is_recommended ?? false),
            features: Array.isArray(p.benefits) ? p.benefits : (p.features || []),
          }));
          setPackages(mapped);
          // 默认选中第一个套餐
          if (mapped.length > 0) setSelectedPlan(mapped[0].id);
        }
      }).catch(() => {}),
      fetchPaymentConfig().then(setPaymentConfig).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const handleBuy = useCallback((pkg: any) => {
    if (!isLoggedIn) {
      window.location.href = `/login?redirect=/vip`;
      return;
    }
    setSelectedPlan(pkg.id);
    setShowPayModal(true);
  }, [isLoggedIn]);

  // 套餐数据（必须在 currentPkg 之前定义，避免 TDZ 运行时错误）
  const displayPackages = packages.length > 0 ? packages : [
    { id: 1, name: "月度VIP", price: "29.9", duration: 30, description: "适合短期需求", is_recommended: false },
    { id: 2, name: "季度VIP", price: "79.9", duration: 90, description: "性价比之选", is_recommended: true },
    { id: 3, name: "年度VIP", price: "239.9", duration: 365, description: "最高性价比", is_recommended: false },
  ];

  // 核心特权（替代对比表，更轻量的展示）
  const privilegeItems = [
    { icon: "ri-pushpin-line", title: "置顶推荐", desc: "信息每日置顶曝光" },
    { icon: "ri-vip-crown-line", title: "加V认证", desc: "官方认证标识" },
    { icon: "ri-infinity-line", title: "无限发布", desc: "不限条数发布信息" },
  ];

  // 支付方式判断
  const showAlipay = !!paymentConfig?.alipay_qrcode && (!!paymentConfig?.qrcode_enabled || !!paymentConfig?.alipay_enabled);
  const showWechat = !!paymentConfig?.wechat_qrcode && (!!paymentConfig?.qrcode_enabled || !!paymentConfig?.wechat_enabled);
  const currentPkg = displayPackages.find((p: any) => p.id === selectedPlan) || displayPackages[0];

  return (
    <div className="min-h-screen bg-[#F4F6F8]">
      <Navbar />

      {/* ── Hero 区：深藏青 ── */}
      <section className="relative bg-gradient-to-br from-[#16283F] via-[#1B3A5C] to-[#2C5A8C] py-16 overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#F08A24]/10 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-white/5 blur-3xl" />

        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 rounded-full text-[#F5A94D] text-xs font-semibold mb-6">
            <i className="ri-vip-crown-line" />
            VIP 会员专属特权
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
            升级<span className="bg-gradient-to-r from-[#F08A24] to-[#F5A94D] bg-clip-text text-transparent">VIP</span>，获得更多特权
          </h1>
          <p className="text-gray-300 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            置顶推荐、无限发布、加V认证，让您的信息获得更多曝光
          </p>

          {/* 特权徽章横排 */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl mx-auto">
            {privilegeItems.map((item, idx) => (
              <div key={idx} className="flex items-center gap-3 p-4 bg-white/5 rounded-xl border border-white/10 backdrop-blur-sm">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#F08A24] to-[#E26A1B] flex items-center justify-center shrink-0">
                  <i className={`${item.icon} text-white text-lg`} />
                </div>
                <div className="text-left">
                  <div className="text-sm font-bold text-white">{item.title}</div>
                  <div className="text-[11px] text-gray-400">{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 套餐卡片 ── */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">选择套餐</h2>
            <p className="text-gray-400 text-sm">选择适合您的VIP方案</p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="animate-pulse bg-white rounded-xl border border-gray-100 p-8">
                  <div className="w-1/2 h-6 bg-gray-200 rounded mx-auto mb-4" />
                  <div className="w-1/3 h-10 bg-gray-200 rounded mx-auto mb-6" />
                  <div className="space-y-3">
                    {[...Array(5)].map((_, j) => (
                      <div key={j} className="w-full h-4 bg-gray-100 rounded" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {displayPackages.map((pkg) => {
                const isRecommended = pkg.is_recommended;
                return (
                  <div
                    key={pkg.id}
                    className={`relative rounded-xl transition-all duration-300 flex flex-col overflow-hidden ${
                      isRecommended
                        ? "bg-gradient-to-b from-[#1B3A5C] to-[#2C5A8C] shadow-2xl shadow-[#1B3A5C]/25 scale-[1.03] z-10 text-white"
                        : "bg-white border-2 border-gray-100 shadow-lg hover:border-[#1B3A5C]/30 hover:shadow-xl"
                    }`}
                  >
                    {isRecommended && (
                      <div className="absolute top-0 inset-x-0 bg-gradient-to-r from-[#F08A24] to-[#E26A1B] text-center py-1.5 text-xs font-bold text-white flex items-center justify-center gap-1">
                        <i className="ri-star-line text-[10px]" />热门推荐
                      </div>
                    )}

                    <div className={`p-8 flex-1 flex flex-col ${isRecommended ? 'pt-10' : ''}`}>
                      <div className="text-center mb-6">
                        <h3 className={`text-lg font-bold mb-1 ${isRecommended ? 'text-white' : 'text-gray-800'}`}>{pkg.name}</h3>
                        <p className={`text-xs ${isRecommended ? 'text-gray-300' : 'text-gray-400'}`}>{pkg.description}</p>
                      </div>

                      <div className="text-center mb-6">
                        <div className="flex items-baseline justify-center gap-1">
                          <span className={`text-sm ${isRecommended ? 'text-gray-300' : 'text-gray-400'}`}>¥</span>
                          <span className={`text-4xl font-extrabold ${isRecommended ? 'text-[#F5A94D]' : 'text-gray-800'}`}>
                            {pkg.price}
                          </span>
                        </div>
                        <p className={`text-xs mt-1 ${isRecommended ? 'text-gray-300' : 'text-gray-400'}`}>
                          {pkg.duration >= 365 ? `仅 ¥${(Number(pkg.price) / 12).toFixed(1)}/月` : `共 ${pkg.duration} 天`}
                        </p>
                      </div>

                      <ul className="space-y-3 mb-8 flex-1">
                        {[
                          "无限发布信息",
                          "每日置顶推荐",
                          "加V官方认证",
                          "20张高清图片",
                          "联系方式完整展示",
                        ].map((feature, fi) => (
                          <li key={fi} className="flex items-start gap-2.5">
                            <i className={`ri-check-line text-sm mt-0.5 ${isRecommended ? 'text-[#F5A94D]' : 'text-green-500'}`} />
                            <span className={`text-sm ${isRecommended ? 'text-gray-200' : 'text-gray-600'}`}>{feature}</span>
                          </li>
                        ))}
                      </ul>

                      <button
                        onClick={() => handleBuy(pkg)}
                        className={`w-full py-3.5 rounded-lg font-bold text-sm transition-all ${
                          isRecommended
                            ? "bg-gradient-to-r from-[#F08A24] to-[#E26A1B] text-white shadow-lg shadow-orange-500/30 hover:from-[#E26A1B] hover:to-[#C95A12]"
                            : "border-2 border-[#1B3A5C]/20 text-[#1B3A5C] hover:bg-[#1B3A5C] hover:text-white hover:border-[#1B3A5C]"
                        }`}
                      >
                        立即开通
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* 底部说明 */}
          <div className="mt-10 text-center">
            <p className="text-xs text-gray-400">
              开通即表示同意{" "}
              <Link to="/page/register-agreement" className="text-[#1B3A5C] hover:text-[#F08A24] transition-colors">服务协议</Link>
              {" "}和{" "}
              <Link to="/page/privacy" className="text-[#1B3A5C] hover:text-[#F08A24] transition-colors">隐私政策</Link>
            </p>
          </div>
        </div>
      </section>

      <UrbanFooter />

      {/* ── 支付弹窗 ── */}
      {showPayModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-800 text-lg">确认支付</h3>
              <button
                onClick={() => setShowPayModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
              >
                <i className="ri-close-line text-gray-500 text-lg" />
              </button>
            </div>

            {/* 套餐信息 */}
            <div className="flex items-center gap-3 mb-5 p-3 bg-[#1B3A5C]/5 rounded-lg">
              <div className="w-10 h-10 rounded-full bg-[#1B3A5C] flex items-center justify-center flex-shrink-0">
                <i className="ri-vip-crown-fill text-[#F5A94D] text-xl" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800">{currentPkg?.name || "VIP套餐"}</p>
                <p className="text-xs text-gray-500">{currentPkg?.duration || 30}天</p>
              </div>
              <p className="text-xl font-bold text-[#E26A1B] flex-shrink-0">
                ¥{currentPkg?.price || "29.9"}
              </p>
            </div>

            {/* 支付方式 */}
            {(showWechat || showAlipay) && (
              <>
                <p className="text-sm font-semibold text-gray-700 mb-3">
                  <i className="ri-arrow-down-circle-line text-[#F08A24] mr-1" />
                  请选择支付方式
                </p>
                <div className="flex gap-3 mb-5">
                  {showWechat && (
                    <button
                      onClick={() => setPayMethod("wechat")}
                      className={`flex-1 flex flex-col items-center gap-2 py-4 rounded-lg text-sm font-medium transition-all cursor-pointer border-2 ${
                        payMethod === "wechat"
                          ? "bg-green-50 text-green-700 border-green-400 shadow-md"
                          : "bg-gray-50 text-gray-500 border-transparent hover:border-gray-300 hover:bg-gray-100"
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xl ${
                        payMethod === "wechat" ? "bg-green-100" : "bg-gray-200"
                      }`}>
                        <i className="ri-wechat-pay-fill" />
                      </div>
                      <span>微信支付</span>
                    </button>
                  )}
                  {showAlipay && (
                    <button
                      onClick={() => setPayMethod("alipay")}
                      className={`flex-1 flex flex-col items-center gap-2 py-4 rounded-lg text-sm font-medium transition-all cursor-pointer border-2 ${
                        payMethod === "alipay"
                          ? "bg-blue-50 text-blue-700 border-blue-400 shadow-md"
                          : "bg-gray-50 text-gray-500 border-transparent hover:border-gray-300 hover:bg-gray-100"
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xl ${
                        payMethod === "alipay" ? "bg-blue-100" : "bg-gray-200"
                      }`}>
                        <i className="ri-alipay-fill" />
                      </div>
                      <span>支付宝</span>
                    </button>
                  )}
                </div>
              </>
            )}

            {/* 收款码展示区 */}
            <div className="text-center">
              {payMethod === "alipay" && showAlipay && (
                <div>
                  <p className="text-xs text-gray-500 mb-3">请使用支付宝扫码付款</p>
                  <img
                    src={paymentConfig?.alipay_qrcode}
                    alt="支付宝收款码"
                    className="w-48 h-48 mx-auto rounded-lg border border-gray-200"
                  />
                </div>
              )}

              {payMethod === "wechat" && showWechat && (
                <div>
                  <p className="text-xs text-gray-500 mb-3">请使用微信扫码付款</p>
                  <img
                    src={paymentConfig?.wechat_qrcode}
                    alt="微信收款码"
                    className="w-48 h-48 mx-auto rounded-lg border border-gray-200"
                  />
                </div>
              )}

              {/* 无收款码占位 */}
              {!showAlipay && !showWechat && (
                <>
                  <div className="w-44 h-44 mx-auto mb-4 bg-gray-100 rounded-lg flex items-center justify-center">
                    <i className="ri-qr-code-line text-5xl text-gray-300" />
                  </div>
                  <p className="text-sm text-gray-500 mb-1">支付功能暂未启用</p>
                  <p className="text-xs text-gray-400">请联系管理员配置收款码</p>
                </>
              )}
            </div>

            <div className="mt-5 p-3.5 bg-[#1B3A5C]/5 rounded-lg text-xs text-[#1B3A5C] text-center font-medium">
              <i className="ri-information-line mr-1" />
              支付成功后将自动开通VIP权益
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
